// src/pages/parametrizacao/ConfiguracaoLoginPanel.tsx
//
// Painel admin do MÉTODO DE LOGIN do tenant. Molde de LogoEmailPanel/RegrasAprovacaoPanel:
// GET on-mount, PUT ao salvar. Editar exige configuration.editar_login; o botão que abre o
// painel é gated por configuration.listar_login em Parametrizacao.tsx (ROLE_MASTER bypassa
// as duas via hasPermission).
//
// LEITURA: a rota ADMINISTRATIVA /detalhes, não a pública da raiz. A pública é anônima e
// responde com Cache-Control de 60s — ela existe para a tela de login, que a consome antes
// de haver token —, então um painel de administração lendo dali podia mostrar por até um
// minuto o valor anterior ao próprio save. O /detalhes não é cacheável e ainda traz a
// trilha de alteração. Após o PUT a trilha vem da resposta do próprio save, que devolve o
// mesmo DTO — sem GET extra.
//
// UI: dois checkboxes (permiteSenha / permiteMicrosoft). Guard: não permite os dois
// desmarcados. Confirmação DUPLA ao salvar "só Microsoft" (desliga a senha para
// todos, inclusive quem configura → risco de lock-out; correção manual no banco).
// Salva mapeando os checkboxes para { metodoLogin }.
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { apiGet, apiPut } from '@/services/api';
import { useAuth } from '@/contexts/AuthContext';
import { showAlert, showConfirm } from '@/contexts/DialogContext';
import { Toast } from '@/contexts/ToastContext';
import SaveButton from '@/components/common/SaveButton';
import { formatDateTime } from '@/utils/formatUtils';
import { toMetodoLogin, fromMetodoLogin, toConfiguracaoLoginDetalhes } from '@/types/configuracaoLogin';
import './ConfiguracaoLoginPanel.css';

interface ConfiguracaoLoginPanelProps {
  theme: 'dark' | 'light';
}

const ENDPOINT = '/v1/configuracao-login';
const ENDPOINT_DETALHES = `${ENDPOINT}/detalhes`;

const ConfiguracaoLoginPanel: React.FC<ConfiguracaoLoginPanelProps> = ({ theme }) => {
  const { hasPermission } = useAuth();
  const podeEditar = hasPermission('configuration.editar_login');

  const [permiteSenha, setPermiteSenha] = useState(true);
  const [permiteMicrosoft, setPermiteMicrosoft] = useState(true);
  // Trilha de alteração: null enquanto nunca houve alteração registrada (o backend
  // devolve 200 com trilha nula nesse caso) — o rodapé simplesmente não aparece.
  const [dataAtualizacao, setDataAtualizacao] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    let cancelado = false;
    const carregar = async () => {
      setIsLoading(true);
      try {
        const res = await apiGet(ENDPOINT_DETALHES);
        if (cancelado) return;
        const detalhes = toConfiguracaoLoginDetalhes(res.data);
        const checkboxes = fromMetodoLogin(detalhes.metodoLogin);
        setPermiteSenha(checkboxes.permiteSenha);
        setPermiteMicrosoft(checkboxes.permiteMicrosoft);
        setDataAtualizacao(detalhes.dataAtualizacao);
      } catch (err) {
        if (cancelado) return;
        if (axios.isAxiosError(err) && err.response?.data?.error) {
          await showAlert(err.response.data.error, { variant: 'error' });
        } else {
          await showAlert('Erro ao carregar configuração de login.', { variant: 'error' });
        }
      } finally {
        if (!cancelado) setIsLoading(false);
      }
    };
    carregar();
    return () => {
      cancelado = true;
    };
  }, []);

  const metodo = toMetodoLogin({ permiteSenha, permiteMicrosoft });
  const nenhumSelecionado = metodo === null;

  const handleSalvar = async () => {
    if (!podeEditar || isSaving) return;
    if (!metodo) {
      await showAlert('Selecione ao menos um método de login.', { variant: 'warning' });
      return;
    }

    // Confirmação dupla ao desligar a senha (resultado "só Microsoft").
    if (!permiteSenha) {
      const c1 = await showConfirm(
        'Ao permitir SOMENTE login Microsoft, o login por senha será desativado para TODOS os usuários deste cliente — inclusive para você. Quem não tiver conta no Microsoft (Entra ID) perde o acesso, e a correção exige intervenção manual no banco. Deseja continuar?',
        { variant: 'warning', title: 'Atenção: risco de perda de acesso' }
      );
      if (!c1) return;
      const c2 = await showConfirm(
        'Confirma que todos os usuários possuem conta Microsoft e que você não perderá o acesso?',
        { variant: 'warning', title: 'Confirmação final' }
      );
      if (!c2) return;
    }

    setIsSaving(true);
    try {
      // O PUT devolve o mesmo DTO do /detalhes — a trilha vem da própria resposta do
      // save, sem uma segunda ida ao servidor.
      const res = await apiPut(ENDPOINT, { metodoLogin: metodo });
      setDataAtualizacao(toConfiguracaoLoginDetalhes(res.data).dataAtualizacao);
      Toast.success('Método de login atualizado.');
    } catch (err) {
      if (axios.isAxiosError(err) && err.response?.data?.error) {
        await showAlert(err.response.data.error, { variant: 'error' });
      } else {
        await showAlert('Erro ao salvar método de login.', { variant: 'error' });
      }
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className={`configuracao-login-panel ${theme}`}>
      {isLoading ? (
        <p>Carregando configuração...</p>
      ) : (
        <>
          <p className="configuracao-login-hint">
            Escolha como os usuários podem entrar. Marque ao menos uma opção.
          </p>

          <label className="configuracao-login-option">
            <input
              type="checkbox"
              checked={permiteSenha}
              disabled={!podeEditar || isSaving}
              onChange={(e) => setPermiteSenha(e.target.checked)}
            />
            <span>Permitir login por usuário e senha</span>
          </label>

          <label className="configuracao-login-option">
            <input
              type="checkbox"
              checked={permiteMicrosoft}
              disabled={!podeEditar || isSaving}
              onChange={(e) => setPermiteMicrosoft(e.target.checked)}
            />
            <span>Permitir login com Microsoft (Entra ID)</span>
          </label>

          {nenhumSelecionado && (
            <p className="configuracao-login-error" role="alert">
              Selecione ao menos um método de login.
            </p>
          )}

          <div className="configuracao-login-actions">
            <SaveButton
              isLoading={isSaving}
              onClick={handleSalvar}
              disabled={!podeEditar || isSaving || nenhumSelecionado}
            />
          </div>

          {!podeEditar && (
            <p className="configuracao-login-hint">
              Você não tem permissão para alterar o método de login.
            </p>
          )}

          {dataAtualizacao && (
            <div className="configuracao-login-footer">
              <span className="configuracao-login-footer-icon" aria-hidden="true">ⓘ</span>
              <span>Última atualização: {formatDateTime(dataAtualizacao)}</span>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ConfiguracaoLoginPanel;
